import React, { useMemo, useState } from 'react';
import './CloudCapacityManagementTable.less';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Radio, Segmented } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import '../CapacityButtons.less';
import AdvancedSearchBar from 'components/ui/AdvancedSearch/AdvancedSearchBar/AdvancedSearchBar';
import { useClusterSizesPerCloudProviders } from 'hooks/capacityManagementQueries';
import {
  ClusterSizesPerClusterCodeItem,
  OperatorPerPlatformItem,
} from 'types/capacityManagement.types';
import { useIpuCost } from 'hooks/useIpuCost';
import CapacitySizeList from '../CapacitySizeList/CapacitySizeList';
import CapacityMatrix from '../CapacityMatrix/CapacityMatrix';
import SizeDrawer from '../SizeDrawer/SizeDrawer';
import BulkEditDrawer, {
  BulkEditTarget,
} from '../BulkEditDrawer/BulkEditDrawer';
import {
  buildSizeSummaries,
  CapacityMatrixRecord,
  findDivergentOperators,
  findOutliers,
  matchesSizeFilter,
  SizeFilter,
} from '../capacitySummary';

type CapacityView = 'list' | 'matrix';

const SIZE_FILTERS: { value: SizeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'differs', label: 'Has differences' },
  { value: 'partial', label: 'Some operators only' },
  { value: 'standard', label: 'Standard' },
  { value: 'custom', label: 'Custom' },
];

type CapacitySearchFilter = {
  field: string;
  values: string[];
};

function normalizeValue(value: unknown) {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase();
  }

  if (typeof value === 'object') {
    return JSON.stringify(value).trim().toLowerCase();
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return `${value}`.trim().toLowerCase();
  }

  return '';
}

type CloudCapacityManagementTableProps = Readonly<{
  operatorsPerPlatform: OperatorPerPlatformItem[];
  cloudProviderID: number;
  cloudProviderKey: string;
  showModalForNewSize: boolean;
  setShowModalForNewSize: React.Dispatch<React.SetStateAction<boolean>>;
  initialAdvancedSearchValue?: string;
  showCellEditModal: boolean;
  setShowCellEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  clickedCellValue:
    (ClusterSizesPerClusterCodeItem & { operatorAlias: string }) | undefined;
  setClickedCellValue: React.Dispatch<
    React.SetStateAction<
      (ClusterSizesPerClusterCodeItem & { operatorAlias: string }) | undefined
    >
  >;
  prefillCellData:
    | (Partial<ClusterSizesPerClusterCodeItem> & {
        rolesIDs?: number[];
        clickedOperatorID?: number;
        clickedOperatorIDs?: number[];
      })
    | undefined;
  setPrefillCellData: React.Dispatch<
    React.SetStateAction<
      | (Partial<ClusterSizesPerClusterCodeItem> & {
          rolesIDs?: number[];
          clickedOperatorID?: number;
          clickedOperatorIDs?: number[];
        })
      | undefined
    >
  >;
  canEdit: boolean;
}>;

function parseCapacitySearchValue(searchValue: string): CapacitySearchFilter[] {
  if (!searchValue?.trim()) {
    return [];
  }

  // Capacity filtering is purely client-side and always ANDs its clauses, so we
  // split on every logical operator (&&, ||, ^^) and ignore the operator tokens.
  return searchValue
    .split(/\|\||\^\^|&&/)
    .map(pair => {
      const trimmedPair = pair.trim();
      if (!trimmedPair.includes('=')) {
        return null;
      }
      const separatorIndex = trimmedPair.indexOf('=');
      const field = trimmedPair.slice(0, separatorIndex);
      const values = trimmedPair.slice(separatorIndex + 1);

      if (!field || values === undefined) {
        return null;
      }

      return {
        field: field.trim().replace(/^'+|'+$/g, ''),
        values: values
          .split(',')
          .map(value => value.trim())
          .filter(Boolean),
      };
    })
    .filter((filter): filter is CapacitySearchFilter => !!filter);
}

function extractCapacityFreeText(value: string): string {
  if (!value?.trim()) return '';
  return value
    .split(/\|\||\^\^|&&/)
    .map(part => part.trim())
    .filter(part => part && !part.includes('='))
    .join(' ');
}

function parseBooleanLikeValue(value: string): boolean | null {
  const normalizedValue = normalizeValue(value);

  if (['t', 'true'].includes(normalizedValue)) {
    return true;
  }

  if (['f', 'false'].includes(normalizedValue)) {
    return false;
  }

  return null;
}

function matchesCapacityValue(
  actualValue: unknown,
  expectedValues: string[],
  like = false,
) {
  const normalizedActualValue = normalizeValue(actualValue);

  return expectedValues.some(expectedValue => {
    const booleanLikeValue = parseBooleanLikeValue(expectedValue);

    if (typeof actualValue === 'boolean' && booleanLikeValue !== null) {
      return actualValue === booleanLikeValue;
    }

    const normalizedExpected = normalizeValue(expectedValue);
    // `like`: substring match, so a partial operator (e.g. "central") matches
    // "gc-us-central1-f-1". Otherwise an exact match.
    return like
      ? normalizedActualValue.includes(normalizedExpected)
      : normalizedActualValue === normalizedExpected;
  });
}

function CloudCapacityManagementTable({
  operatorsPerPlatform,
  cloudProviderID,
  cloudProviderKey,
  showModalForNewSize,
  setShowModalForNewSize,
  initialAdvancedSearchValue,
  showCellEditModal,
  setShowCellEditModal,
  clickedCellValue,
  setClickedCellValue,
  prefillCellData,
  setPrefillCellData,
  canEdit,
}: CloudCapacityManagementTableProps) {
  const prefillProviderID = prefillCellData?.regionProvider?.cloudProviderID;
  const shouldOpenForCurrentProvider =
    !prefillProviderID || prefillProviderID === cloudProviderID;
  const openModalForNewSize =
    showModalForNewSize && !!cloudProviderID && shouldOpenForCurrentProvider;
  const {
    data: clusterSizesPerCloudProviders,
    isLoading: isClusterSizesPerCloudProvidersLoading,
  } = useClusterSizesPerCloudProviders({
    cloudProviderID: cloudProviderID.toString(),
  });
  const { showIpuCost, costConfig } = useIpuCost();
  const [sizeFilter, setSizeFilter] = useState<SizeFilter>('all');
  const [expandedSizes, setExpandedSizes] = useState<string[]>([]);
  const [bulkEditTarget, setBulkEditTarget] = useState<BulkEditTarget>();
  const [showSignals, setShowSignals] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const view: CapacityView =
    searchParams.get('view') === 'matrix' ? 'matrix' : 'list';
  const setView = (nextView: CapacityView) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (nextView === 'matrix') next.set('view', 'matrix');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );
  };
  // Keep the filter in the URL `q` param (like the Clusters page) so it survives
  // refresh, is shareable, and clears on reset. Falls back to the deep-link value.
  const advancedSearchValue =
    searchParams.get('q') ?? (initialAdvancedSearchValue || '');
  const setAdvancedSearchValue = (value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        // The bar now owns the filter, so drop the one-shot deep-link params
        // (operator/sizeName from the Clusters "size" link). Otherwise they keep
        // re-seeding initialAdvancedSearchValue and the X button can't clear them.
        next.delete('operator');
        next.delete('sizeName');
        if (value) next.set('q', value);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  };

  const advancedFilters = useMemo(
    () => parseCapacitySearchValue(advancedSearchValue),
    [advancedSearchValue],
  );

  const searchTerm = useMemo(
    () => extractCapacityFreeText(advancedSearchValue),
    [advancedSearchValue],
  );

  // Opens "Create size" prefilled from an operator that already has this size, with the given
  // operators ticked in its Operators step.
  function openCreateForOperators(
    record: CapacityMatrixRecord,
    operatorClusterCodes: string[],
  ) {
    const peerKey = Object.keys(record).find(
      key => key !== 'size' && record[key],
    );
    const peer = peerKey
      ? (record[peerKey] as ClusterSizesPerClusterCodeItem)
      : undefined;

    const clickedOperatorIDs = operatorsPerPlatform
      .filter(op => operatorClusterCodes.includes(op.clusterCode))
      .map(op => op.id);
    const clickedOperatorID = clickedOperatorIDs[0];

    setPrefillCellData(
      peer
        ? {
            name: peer.name,
            displayName: peer.displayName,
            cpu: peer.cpu,
            dsize: peer.dsize,
            icc: peer.icc,
            ipu: peer.ipu,
            memoryRequest: peer.memoryRequest,
            memoryLimit: peer.memoryLimit,
            memoryText: peer.memoryText,
            nodeLabel: peer.nodeLabel,
            nodeLabelObj: peer.nodeLabelObj,
            overCommitment: peer.overCommitment,
            requireHyperDisk: peer.requireHyperDisk,
            roles: peer.roles,
            rolesIDs: peer.roles?.map(r => r.role.id),
            regionProvider: peer.regionProvider,
            istioConfigEnabled: peer.istioConfigEnabled,
            istioProxyConcurrency: peer.istioProxyConcurrency,
            istioProxyCpuRequest: peer.istioProxyCpuRequest,
            istioProxyCpuLimit: peer.istioProxyCpuLimit,
            istioProxyMemoryRequest: peer.istioProxyMemoryRequest,
            istioProxyMemoryLimit: peer.istioProxyMemoryLimit,
            queryRequestsPerMinute: peer.queryRequestsPerMinute,
            queryMaxConcurrentRequests: peer.queryMaxConcurrentRequests,
            clickedOperatorID,
            clickedOperatorIDs,
          }
        : { clickedOperatorID, clickedOperatorIDs },
    );
    setShowCellEditModal(false);
    setShowModalForNewSize(true);
  }

  // A size an operator already has opens the full per-operator editor; a missing one opens
  // "Create size" for that operator.
  function handleCellClick(
    record: CapacityMatrixRecord,
    operatorClusterCode: string,
  ) {
    const cellValue = record[operatorClusterCode] as
      ClusterSizesPerClusterCodeItem | undefined;

    if (!cellValue) {
      openCreateForOperators(record, [operatorClusterCode]);
      return;
    }

    setShowCellEditModal(true);
    setClickedCellValue({
      ...cellValue,
      operatorAlias: operatorClusterCode,
    });
  }

  const visibleMatrix = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();
    const records = (clusterSizesPerCloudProviders ??
      []) as CapacityMatrixRecord[];

    const visibleCellMap = new Map<string, boolean>();

    const doesRowMatchSearch = (record: CapacityMatrixRecord) => {
      if (!searchValue) {
        return false;
      }

      return normalizeValue(record.size).includes(searchValue);
    };

    const doesOperatorMatchSearch = (operator: OperatorPerPlatformItem) => {
      if (!searchValue) {
        return false;
      }

      return (
        normalizeValue(operator.clusterCode).includes(searchValue) ||
        normalizeValue(operator.region).includes(searchValue)
      );
    };

    const doesCellMatchSearch = (
      cellValue: ClusterSizesPerClusterCodeItem | undefined,
    ) => {
      if (!searchValue || !cellValue) {
        return false;
      }

      return [cellValue.name, cellValue.displayName].some(value =>
        normalizeValue(value).includes(searchValue),
      );
    };

    const rowFilters = advancedFilters.filter(
      filter => filter.field === 'sizeName',
    );
    const operatorFilters = advancedFilters.filter(filter =>
      ['operatorClusterCode', 'operatorRegion'].includes(filter.field),
    );
    const cellFilters = advancedFilters.filter(
      filter =>
        !['sizeName', 'operatorClusterCode', 'operatorRegion'].includes(
          filter.field,
        ),
    );

    const doesRowMatchAdvancedFilters = (record: CapacityMatrixRecord) =>
      rowFilters.every(filter =>
        matchesCapacityValue(record.size, filter.values),
      );

    const doesOperatorMatchAdvancedFilters = (
      operator: OperatorPerPlatformItem,
    ) =>
      operatorFilters.every(filter => {
        if (filter.field === 'operatorClusterCode') {
          return matchesCapacityValue(
            operator.clusterCode,
            filter.values,
            true,
          );
        }

        if (filter.field === 'operatorRegion') {
          return matchesCapacityValue(operator.region, filter.values, true);
        }

        return true;
      });

    const doesCellMatchAdvancedFilters = (
      cellValue: ClusterSizesPerClusterCodeItem | undefined,
    ) =>
      cellFilters.every(filter => {
        if (!cellValue) {
          return false;
        }

        switch (filter.field) {
          case 'sizeDisplayName':
            return matchesCapacityValue(cellValue.displayName, filter.values);
          case 'cpu':
            return matchesCapacityValue(cellValue.cpu, filter.values);
          case 'memoryRequest':
            return matchesCapacityValue(cellValue.memoryRequest, filter.values);
          case 'memoryLimit':
            return matchesCapacityValue(cellValue.memoryLimit, filter.values);
          case 'memoryText':
            return matchesCapacityValue(cellValue.memoryText, filter.values);
          case 'ipu':
            return matchesCapacityValue(cellValue.ipu, filter.values);
          case 'icc':
            return matchesCapacityValue(cellValue.icc, filter.values);
          case 'dsize':
            return matchesCapacityValue(cellValue.dsize, filter.values);
          case 'canBeDeleted':
            return matchesCapacityValue(cellValue.canBeDeleted, filter.values);
          case 'isCustomSize':
            return matchesCapacityValue(cellValue.isCustomSize, filter.values);
          case 'requireHyperDisk':
            return matchesCapacityValue(
              cellValue.requireHyperDisk || false,
              filter.values,
            );
          case 'istioConfigEnabled':
            return matchesCapacityValue(
              cellValue.istioConfigEnabled || false,
              filter.values,
            );
          default:
            return true;
        }
      });

    records.forEach(record => {
      operatorsPerPlatform.forEach(operator => {
        const cellValue = record[operator.clusterCode] as
          ClusterSizesPerClusterCodeItem | undefined;

        const searchMatches =
          !searchValue ||
          doesRowMatchSearch(record) ||
          doesOperatorMatchSearch(operator) ||
          doesCellMatchSearch(cellValue);

        const advancedMatches =
          doesRowMatchAdvancedFilters(record) &&
          doesOperatorMatchAdvancedFilters(operator) &&
          doesCellMatchAdvancedFilters(cellValue);

        visibleCellMap.set(
          `${record.size}::${operator.clusterCode}`,
          searchMatches && advancedMatches,
        );
      });
    });

    const visibleRows = records.filter(record =>
      operatorsPerPlatform.some(operator =>
        visibleCellMap.get(`${record.size}::${operator.clusterCode}`),
      ),
    );

    const visibleOperators = operatorsPerPlatform.filter(operator =>
      visibleRows.some(record =>
        visibleCellMap.get(`${record.size}::${operator.clusterCode}`),
      ),
    );

    return {
      records: visibleRows,
      operators: visibleOperators,
    };
  }, [
    advancedFilters,
    clusterSizesPerCloudProviders,
    operatorsPerPlatform,
    searchTerm,
  ]);

  // Signals use every size and operator, so a search doesn't hide them.
  const allSummaries = useMemo(
    () =>
      buildSizeSummaries(
        (clusterSizesPerCloudProviders ?? []) as CapacityMatrixRecord[],
        operatorsPerPlatform,
      ),
    [clusterSizesPerCloudProviders, operatorsPerPlatform],
  );
  const divergentOperators = useMemo(
    () => findDivergentOperators(allSummaries),
    [allSummaries],
  );
  const outliers = useMemo(() => findOutliers(allSummaries), [allSummaries]);

  const searchedSummaries = useMemo(
    () => buildSizeSummaries(visibleMatrix.records, visibleMatrix.operators),
    [visibleMatrix],
  );
  const filterCounts = useMemo(
    () =>
      Object.fromEntries(
        SIZE_FILTERS.map(({ value }) => [
          value,
          searchedSummaries.filter(summary => matchesSizeFilter(summary, value))
            .length,
        ]),
      ) as Record<SizeFilter, number>,
    [searchedSummaries],
  );
  const shownSummaries = searchedSummaries.filter(summary =>
    matchesSizeFilter(summary, sizeFilter),
  );

  function reviewOutlier(size: string) {
    setSizeFilter('all');
    setView('list');
    setExpandedSizes(prev => (prev.includes(size) ? prev : [...prev, size]));
  }

  const editedOperator = operatorsPerPlatform.find(
    operator => operator.clusterCode === clickedCellValue?.operatorAlias,
  );

  const hasSignals = divergentOperators.length > 0 || outliers.length > 0;

  return (
    <div className="cloud-capacity-management-table__wrapper">
      <div className="cloud-capacity-management-table__search">
        <AdvancedSearchBar
          model="CloudCapacity"
          value={advancedSearchValue}
          onSearch={setAdvancedSearchValue}
          placeholder="Search operators, sizes, or add a filter…"
        />
      </div>

      {showSignals && hasSignals && !isClusterSizesPerCloudProvidersLoading && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setShowSignals(false)}
          className="cloud-capacity-management-table__signals"
          message="Worth a look"
          description={
            <ul className="cloud-capacity-management-table__signal-list">
              {divergentOperators.map(operator => (
                <li key={operator.clusterCode}>
                  <span>
                    <b className="cloud-capacity-management-table__mono">
                      {operator.clusterCode}
                    </b>{' '}
                    differs from the baseline on {operator.differing} of the{' '}
                    {operator.offered} sizes it shares with other operators.
                  </span>
                  <Button
                    className="capacity-btn capacity-btn--sm capacity-btn--link"
                    onClick={() => {
                      setSizeFilter('all');
                      setView('matrix');
                    }}
                  >
                    See it in the matrix{' '}
                    <ArrowRightOutlined aria-hidden="true" />
                  </Button>
                </li>
              ))}
              {outliers.map(outlier => (
                <li key={`${outlier.size}::${outlier.operator}`}>
                  <span>
                    <b className="cloud-capacity-management-table__mono">
                      {outlier.size}
                    </b>{' '}
                    on{' '}
                    <b className="cloud-capacity-management-table__mono">
                      {outlier.operator}
                    </b>{' '}
                    has a {outlier.value.memoryLimit} GB limit against a{' '}
                    {outlier.baseline.memoryLimit} GB baseline.
                  </span>
                  <Button
                    className="capacity-btn capacity-btn--sm capacity-btn--link"
                    onClick={() => reviewOutlier(outlier.size)}
                  >
                    Review <ArrowRightOutlined aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          }
        />
      )}

      <div className="cloud-capacity-management-table__toolbar">
        <Radio.Group
          aria-label="Filter sizes"
          optionType="button"
          value={sizeFilter}
          onChange={event => setSizeFilter(event.target.value)}
          options={SIZE_FILTERS.map(({ value, label }) => ({
            value,
            label: `${label} · ${filterCounts[value] ?? 0}`,
          }))}
        />
        <Segmented
          aria-label="View"
          value={view}
          onChange={value => setView(value as CapacityView)}
          options={[
            { label: 'List', value: 'list' },
            { label: 'Matrix', value: 'matrix' },
          ]}
        />
      </div>

      {view === 'list' ? (
        <CapacitySizeList
          summaries={shownSummaries}
          loading={isClusterSizesPerCloudProvidersLoading}
          canEdit={canEdit}
          showIpuCost={showIpuCost}
          costConfig={costConfig}
          expandedSizes={expandedSizes}
          onExpandedSizesChange={setExpandedSizes}
          onCellClick={handleCellClick}
          onBulkEdit={setBulkEditTarget}
          onAddMissing={openCreateForOperators}
        />
      ) : (
        <CapacityMatrix
          summaries={shownSummaries}
          operators={visibleMatrix.operators}
          loading={isClusterSizesPerCloudProvidersLoading}
          canEdit={canEdit}
          onCellClick={handleCellClick}
        />
      )}

      {bulkEditTarget && (
        <BulkEditDrawer
          // Remount per target so the form starts from that selection's values.
          key={`${bulkEditTarget.size}:${bulkEditTarget.cells
            .map(({ clusterCode }) => clusterCode)
            .join(
              ',',
            )}:${bulkEditTarget.startFromBaseline ? 'baseline' : 'edit'}`}
          target={bulkEditTarget}
          canEdit={canEdit}
          onClose={() => setBulkEditTarget(undefined)}
        />
      )}

      {showCellEditModal && editedOperator && clickedCellValue && (
        <SizeDrawer
          key={`edit:${clickedCellValue.id}`}
          target={{
            kind: 'edit',
            cell: clickedCellValue,
            operator: editedOperator,
            baseline: allSummaries.find(
              summary => summary.size === clickedCellValue.name,
            )?.baseline,
          }}
          operators={operatorsPerPlatform}
          cloudProviderID={cloudProviderID}
          canEdit={canEdit}
          onClose={() => setShowCellEditModal(false)}
        />
      )}

      {!showCellEditModal && openModalForNewSize && (
        <SizeDrawer
          key={`create:${prefillCellData?.name ?? 'new'}:${(
            prefillCellData?.clickedOperatorIDs ?? []
          ).join(',')}`}
          target={{ kind: 'create', prefill: prefillCellData }}
          operators={operatorsPerPlatform}
          cloudProviderID={cloudProviderID}
          canEdit={canEdit}
          onClose={() => {
            setShowModalForNewSize(false);
            setPrefillCellData(undefined);
          }}
        />
      )}
    </div>
  );
}

export default CloudCapacityManagementTable;
